import React, { type ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import { BaseLayout } from "../../Components/BaseLayout";
import { Header } from "../../Components/Header";
import { Breadcrumb } from "../../Components/Breadcrumb";
import { HeroPanel } from "../../Components/HeroPanel";
import { Footer } from "../../Components/Footer";
import { SkipToContent } from "../../Components/SkipToContent";
import { Toolbar } from "../Toolbar";
import type { IconTypes } from "../../Icons";

export interface Props {
  dataTestId?: string;
  children: ReactNode;
  me?: User;
  text: string;
  signOut?: (...props: any[]) => void;
  pageConfig?: {
    breadcrumb?: ReturnType<typeof Breadcrumb>;
    heroPanel?: ReturnType<typeof HeroPanel>;
    pageColNumber?: number;
  };
  headerConfig?: {
    description?: string;
    navigationLinks?: ItemList[];
    navigationLinksCurrentPath?: string;
    serviceLinkItems?: ItemList[];
    toolbar?: ReturnType<typeof Toolbar>;
  };
}

export interface ItemList {
  text: string;
  iconType?: IconTypes;
  url?: string;
  attributes?: AttributesProps; //?Author Specific attribute
  errorMarker?: boolean;
  errorCount?: number;
}
export interface AttributesProps {
  onClick?: () => void;
}

export interface User {
  id: ReturnType<typeof uuidv4>;
  name?: string;
  picture?: string;
  email?: string;
  displayName: string;
  admin: boolean;
}

export const MainLayout = (props: Props) => {
  const accountLinks: ItemList[] = props.me
    ? [
        {
          text: props.me.displayName,
          iconType: "AccountIcon",
        },
        {
          text: "Sign Out",
          url: "#",
          attributes: { onClick: props.signOut },
        },
      ]
    : [];
  const serviceLinksItemList: ItemList[] = [
    ...(props.headerConfig?.serviceLinkItems || []),
    ...accountLinks,
  ];

  const serviceLinks = {
    id: "service-links",
    dataTestId: "service-links-test",
    itemsList: serviceLinksItemList,
    toggleServicesButton: {
      id: "toggle-services-menu",
      text: "Account",
      ariaLabel: "Toggle services menu",
      dataTestId: "toggle-services-menu",
    },
  };

  const mainNavigation = props.headerConfig?.navigationLinks && {
    id: "main-nav",
    ariaLabel: "Main menu",
    currentPath: props.headerConfig.navigationLinksCurrentPath || "",
    dataTestId: "main-menu-navigation",
    itemList: props.headerConfig.navigationLinks,
    toggleNavigationButton: {
      id: "toggle-main-menu",
      text: "Menu",
      ariaLabel: "Toggle Main Menu",
      dataTestId: "toggle-main-menu",
    },
  };

  const headerProps = {
    text: props.text,
    description: props.headerConfig?.description,
    serviceLinks: serviceLinks,
    navigation: mainNavigation,
    toolbar: props.headerConfig?.toolbar,
    dataTestId: "header-service-links",
  };

  const skipToContentProps = {
    id: "skip-to-content",
    url: "#main-content",
    dataTestId: "test-skip-to-content",
  };

  return (
    <BaseLayout
      dataTestId={props.dataTestId}
      pageConfig={{
        skipToContent: <SkipToContent {...skipToContentProps} />,
        header: <Header variants="internal" {...headerProps} />,
        heroPanel: props.pageConfig?.heroPanel,
        breadcrumb: props.pageConfig?.breadcrumb,
        pageColNumber: props.pageConfig?.pageColNumber,
        footer: (
          <Footer
            id="footer-main-layout"
            dataTestId="footer-main-layout-data-test-id"
          />
        ),
      }}
    >
      {props.children}
    </BaseLayout>
  );
};
